// Sound effects utility - generates sounds using Web Audio API
const AudioCtx = window.AudioContext || window.webkitAudioContext;
let audioCtx = null;

function getCtx() {
  if (!audioCtx) audioCtx = new AudioCtx();
  return audioCtx;
}

function playTone(frequency, duration, type = 'sine', volume = 0.3) {
  try {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch (e) {
    // Silently fail if audio not available
  }
}

// Cute task completion chime - ascending happy notes
export function playTaskComplete() {
  const ctx = getCtx();
  const now = ctx.currentTime;
  [523, 659, 784].forEach((freq, i) => {
    setTimeout(() => playTone(freq, 0.2, 'sine', 0.25), i * 100);
  });
}

// Satisfying ding for general success
export function playDing() {
  playTone(880, 0.3, 'sine', 0.2);
}

// Soft bell for timer end - calming
export function playTimerEnd() {
  const notes = [523, 659, 784, 1047];
  notes.forEach((freq, i) => {
    setTimeout(() => playTone(freq, 0.5, 'sine', 0.2), i * 200);
  });
  // Final resonant bell
  setTimeout(() => playTone(1047, 1.0, 'sine', 0.15), 800);
}

// Loud attention-grabbing alarm for focus session end — plays regardless of sound toggle
export function playFocusAlarm() {
  try {
    const ctx = getCtx();
    // Resume context in case it was suspended (tab in background, etc.)
    if (ctx.state === 'suspended') ctx.resume();
  } catch { /* ignore */ }
  // 4 loud double-beeps: high-low pairs that bounce — louder volume (0.5)
  const pattern = [
    { f: 880, t: 0 },     { f: 660, t: 180 },
    { f: 880, t: 500 },   { f: 660, t: 680 },
    { f: 880, t: 1000 },  { f: 660, t: 1180 },
    { f: 880, t: 1500 },  { f: 660, t: 1680 },
  ];
  pattern.forEach(({ f, t }) => {
    setTimeout(() => playTone(f, 0.18, 'square', 0.5), t);
  });
  // Finishing chime
  setTimeout(() => playTone(1320, 0.6, 'sine', 0.4), 2100);
}

// Pop sound for adding items
export function playPop() {
  playTone(600, 0.08, 'sine', 0.3);
  setTimeout(() => playTone(900, 0.08, 'sine', 0.2), 50);
}

// Gentle notification chime
export function playNotification() {
  [660, 880].forEach((freq, i) => {
    setTimeout(() => playTone(freq, 0.2, 'sine', 0.2), i * 150);
  });
}

// Level up fanfare
export function playLevelUp() {
  const notes = [523, 587, 659, 784, 880, 1047];
  notes.forEach((freq, i) => {
    setTimeout(() => playTone(freq, 0.25, 'triangle', 0.25), i * 80);
  });
}

// Evolution ceremony sound
export function playEvolution() {
  const notes = [392, 440, 523, 587, 659, 784, 880, 1047, 1175, 1319];
  notes.forEach((freq, i) => {
    setTimeout(() => playTone(freq, 0.3, 'sine', 0.2), i * 120);
  });
}

// Streak milestone sound
export function playStreakMilestone() {
  [523, 659, 784, 1047, 1319].forEach((freq, i) => {
    setTimeout(() => playTone(freq, 0.3, 'triangle', 0.2), i * 100);
  });
}

// Check if sounds are enabled (reads from localStorage)
export function isSoundEnabled() {
  return localStorage.getItem('sound_enabled') !== 'false';
}

export function setSoundEnabled(enabled) {
  localStorage.setItem('sound_enabled', enabled ? 'true' : 'false');
}

// Wrapper that checks if sounds are enabled before playing
export function playSoundIfEnabled(soundFn) {
  if (isSoundEnabled()) {
    soundFn();
  }
}

// Focus alarm settings — separate from general sound toggle (defaults ON, loud)
export function isFocusAlarmEnabled() {
  return localStorage.getItem('focusAlarmEnabled') !== 'false';
}

export function setFocusAlarmEnabled(enabled) {
  localStorage.setItem('focusAlarmEnabled', String(enabled));
}
